/**
 * PersonalAssistantDemo — Primary use case: emails, meetings, day-to-day job easier.
 * Advanced demo with Email Draft, Meeting Prep, Task Triage, and Day-at-a-Glance.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Stack,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email as EmailIcon,
  Event as MeetingIcon,
  CheckCircle as TaskIcon,
  Today as DayIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const TONE_OPTIONS = ['Professional', 'Casual', 'Friendly', 'Formal', 'Urgent'];

// Simulated AI responses for demo
const mockEmailDraft = (to, purpose, tone, points) =>
  `Subject: ${purpose}\n\nHi ${to || '[Recipient]'},\n\n${points || 'I wanted to reach out regarding a matter that requires your attention.'}\n\nThank you for your time.\n\nBest regards`;

const mockMeetingPrep = (title, attendees, agenda) => ({
  talkingPoints: [
    'Introduce the main objectives and desired outcomes',
    `Review ${agenda || 'key topics'} with context for each attendee`,
    'Assign action items and next steps',
    'Confirm follow-up meeting if needed',
  ],
  questions: [
    'What’s the top priority we need to address today?',
    'Are there any blockers the team should know about?',
    'What’s the timeline for deliverables?',
  ],
});

const mockTaskTriage = (tasks) =>
  tasks.map((t, i) => ({ ...t, priority: i + 1, suggestedBlock: `${9 + i}:00 - ${10 + i}:00` }));

const SAMPLE_DAY = {
  meetings: [
    { time: '9:00', title: 'Standup', duration: '15 min', attendees: 'Engineering' },
    { time: '11:00', title: 'Product Review', duration: '45 min', attendees: 'Product, Design' },
    { time: '2:00', title: '1:1 with Sarah', duration: '30 min' },
  ],
  focus: '10:00–11:00, 3:00–5:00',
  topTasks: ['Review PRs', 'Draft Q3 plan', 'Customer follow-up'],
};

export default function PersonalAssistantDemo() {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your AI assistant for emails, meetings, and daily work. Choose a mode below.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? 'scrollable' : 'standard'}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab icon={<EmailIcon />} iconPosition="start" label="Email draft" />
        <Tab icon={<MeetingIcon />} iconPosition="start" label="Meeting prep" />
        <Tab icon={<TaskIcon />} iconPosition="start" label="Task triage" />
        <Tab icon={<DayIcon />} iconPosition="start" label="Day at a glance" />
      </Tabs>

      {tab === 0 && <EmailDraftSection />}
      {tab === 1 && <MeetingPrepSection />}
      {tab === 2 && <TaskTriageSection />}
      {tab === 3 && <DayAtGlanceSection />}
    </Box>
  );
}

function EmailDraftSection() {
  const [to, setTo] = useState('');
  const [purpose, setPurpose] = useState('');
  const [tone, setTone] = useState('Professional');
  const [points, setPoints] = useState('');
  const [draft, setDraft] = useState(null);

  const handleGenerate = () => {
    setDraft(mockEmailDraft(to, purpose || 'Follow-up', tone, points || undefined));
  };

  const handleCopy = () => {
    draft && navigator.clipboard.writeText(draft);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Draft an email from context
      </Typography>
      <TextField label="To (name or role)" value={to} onChange={(e) => setTo(e.target.value)} fullWidth size="small" placeholder="e.g. Team lead, Client" />
      <TextField label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} fullWidth size="small" placeholder="e.g. Request time off, Project update" />
      <FormControl fullWidth size="small">
        <InputLabel>Tone</InputLabel>
        <Select value={tone} label="Tone" onChange={(e) => setTone(e.target.value)}>
          {TONE_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField label="Key points to include" value={points} onChange={(e) => setPoints(e.target.value)} fullWidth multiline rows={2} size="small" placeholder="Optional: bullets or summary" />
      <Button variant="contained" startIcon={<SendIcon />} onClick={handleGenerate}>
        Generate draft
      </Button>
      {draft && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">Draft</Typography>
            <Button size="small" startIcon={<CopyIcon />} onClick={handleCopy}>Copy</Button>
          </Box>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{draft}</Typography>
        </Paper>
      )}
    </Stack>
  );
}

function MeetingPrepSection() {
  const [title, setTitle] = useState('');
  const [attendees, setAttendees] = useState('');
  const [agenda, setAgenda] = useState('');
  const [result, setResult] = useState(null);

  const handleGenerate = () => {
    setResult(mockMeetingPrep(title, attendees, agenda));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Get talking points and questions for your meeting
      </Typography>
      <TextField label="Meeting title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" placeholder="e.g. Q3 Planning Sync" />
      <TextField label="Attendees" value={attendees} onChange={(e) => setAttendees(e.target.value)} fullWidth size="small" placeholder="e.g. Product, Engineering leads" />
      <TextField label="Agenda / Key topics" value={agenda} onChange={(e) => setAgenda(e.target.value)} fullWidth multiline rows={2} size="small" placeholder="e.g. Roadmap, resource allocation" />
      <Button variant="contained" startIcon={<MeetingIcon />} onClick={handleGenerate}>
        Get prep
      </Button>
      {result && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Talking points</Typography>
            <List dense>
              {result.talkingPoints.map((p, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><Chip label={i + 1} size="small" /></ListItemIcon>
                  <ListItemText primary={p} />
                </ListItem>
              ))}
            </List>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Questions to ask</Typography>
            <List dense>
              {result.questions.map((q, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText primary={q} secondary="" />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}

function TaskTriageSection() {
  const [tasks, setTasks] = useState(['Review design feedback', 'Send status update to client', 'Plan sprint']);
  const [newTask, setNewTask] = useState('');
  const [triaged, setTriaged] = useState(null);

  const handleAdd = () => {
    if (newTask.trim()) {
      setTasks([...tasks, newTask.trim()]);
      setNewTask('');
    }
  };

  const handleRemove = (i) => {
    setTasks(tasks.filter((_, j) => j !== i));
  };

  const handleTriage = () => {
    setTriaged(mockTaskTriage(tasks.map((t) => ({ label: t }))));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Prioritize tasks and get suggested time blocks
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField label="Add task" value={newTask} onChange={(e) => setNewTask(e.target.value)} fullWidth size="small" onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>Add</Button>
      </Stack>
      <List dense>
        {tasks.map((t, i) => (
          <ListItem key={i} secondaryAction={<Button size="small" onClick={() => handleRemove(i)}><DeleteIcon fontSize="small" /></Button>}>
            <ListItemIcon sx={{ minWidth: 36 }}><TaskIcon color="action" /></ListItemIcon>
            <ListItemText primary={t} />
          </ListItem>
        ))}
      </List>
      <Button variant="contained" startIcon={<TaskIcon />} onClick={handleTriage}>
        Prioritize & suggest blocks
      </Button>
      {triaged && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Prioritized + time blocks</Typography>
          <List dense>
            {triaged.map((t, i) => (
              <ListItem key={i} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}><Chip label={t.priority} size="small" color="primary" /></ListItemIcon>
                <ListItemText primary={t.label} secondary={t.suggestedBlock} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Stack>
  );
}

function DayAtGlanceSection() {
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Your day at a glance
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">Meetings</Typography>
          <List dense>
            {SAMPLE_DAY.meetings.map((m, i) => (
              <ListItem key={i} sx={{ py: 0 }}>
                <ListItemText primary={`${m.time} — ${m.title}`} secondary={`${m.duration} • ${m.attendees}`} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" color="text.secondary">Focus time</Typography>
          <Typography variant="body2">{SAMPLE_DAY.focus}</Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" color="text.secondary">Top 3 tasks</Typography>
          <List dense>
            {SAMPLE_DAY.topTasks.map((t, i) => (
              <ListItem key={i} sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}><Chip label={i + 1} size="small" /></ListItemIcon>
                <ListItemText primary={t} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
      <Typography variant="caption" color="text.secondary">
        In production, this syncs with your calendar and task lists.
      </Typography>
    </Stack>
  );
}
